import { useApolloClient, gql, TypedDocumentNode } from '@apollo/client'
import {
  getMagentoErrorCategory,
  ExceptionCategories,
} from '@emico-utils/magento-graphql-error-categories'
import { useCallback, useState } from 'react'

import {
  RequestPasswordResetEmailMutation,
  RequestPasswordResetEmailMutationVariables,
} from './useRequestPasswordResetEmail.generated'

const mutation = gql`
  mutation RequestPasswordResetEmail($email: String!) {
    requestPasswordResetEmail(email: $email)
  }
` as TypedDocumentNode<
  RequestPasswordResetEmailMutation,
  RequestPasswordResetEmailMutationVariables
>

/**
 * Mutation to request a password reset for an account, based on email address
 * Project example:
 * ```
 *   const { requestPasswordResetEmail, isSuccess, error, isEmailInvalid } = useRequestPasswordResetEmail()
 * ```
 */

export function useRequestPasswordResetEmail() {
  const client = useApolloClient()
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [isEmailInvalid, setIsEmailInvalid] = useState<boolean>(false)

  const submit = useCallback(
    /**
     *
     * @param email email address to request password reset for
     */
    async (email: string) => {
      setIsEmailInvalid(false)
      setIsSuccess(false)
      setError(null)

      try {
        await client.mutate({
          mutation,
          variables: {
            email,
          },
        })

        setIsSuccess(true)
      } catch (error) {
        const errorCategory = getMagentoErrorCategory(error)

        // If the input is incorrect, there could be two causes:
        // the email format is incorrect or the email address doesn't have an account password to request
        if (errorCategory === ExceptionCategories.INPUT && email !== '') {
          setIsEmailInvalid(true)
        }

        if (error instanceof Error) {
          setError(error)
        }
      }
    },
    [client],
  )

  return {
    requestPasswordResetEmail: submit,
    isSuccess,
    error,
    isEmailInvalid,
  }
}
